import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { Tokens } from '../models/auth';
import { AuthService } from './auth.service';
import { EmbeddedAuthService } from './embedded-auth.service';
import { EmbeddedContextService } from './embedded-context.service';
import { EmbeddedTokenService } from './embedded-token.service';
import { TokenService } from './token.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>( null );

  constructor(
    private readonly tokenService: TokenService,
    private readonly embeddedTokenService: EmbeddedTokenService,
    private readonly authService: AuthService,
    private readonly embeddedAuthService: EmbeddedAuthService,
    private readonly embeddedContextService: EmbeddedContextService,
    private readonly router: Router
  ) { }

  intercept( req: HttpRequest<Object>, next: HttpHandler ): Observable<HttpEvent<Object>> {
    let authReq = req;

    const token = this.getToken();

    if ( token != null ) {
      authReq = this.addTokenHeader( req, token );
    }

    return next.handle( authReq ).pipe( catchError( error => {
      if ( error.status === 401 ) {
        if ( this.isEmbeddedRoute() && authReq.url.includes( 'exchange' ) ) {
          this.embeddedContextService.setCodeExchangeFailed();
          return throwError( () => error );
        }

        if ( authReq.url.includes( 'refresh' ) ) {
          this.isRefreshing = false;

          this.handleRefreshFailure();
        }
        else {
          return this.handle401Error( authReq, next );
        }
      }

      return throwError( () => error );
    } ) );
  }

  private handle401Error( request: HttpRequest<Object>, next: HttpHandler ) {
    const handleRefreshingErrors = () => {
      this.isRefreshing = false;
      this.handleRefreshFailure();
    }

    if ( !this.isRefreshing ) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next( null );

      const token = this.getToken();
      const refreshToken = this.getRefreshToken();

      if ( token && refreshToken ) {
        return this.refreshToken( { token, refreshToken } ).pipe(
          switchMap( ( res: Tokens ) => {
            this.isRefreshing = false;

            this.saveToken( res.token );
            this.saveRefreshToken( res.refreshToken );
            this.refreshTokenSubject.next( res.token );

            return next.handle( this.addTokenHeader( request, res.token ) );
          } ),
          catchError( ( error ) => {
            handleRefreshingErrors();
            return throwError( () => error );
          } )
        );
      }
      else {
        handleRefreshingErrors();
      }
    }

    return this.refreshTokenSubject.pipe(
      filter( token => token !== null ),
      take( 1 ),
      switchMap( ( token ) => next.handle( this.addTokenHeader( request, token ) ) ),
      catchError( ( error ) => {
        handleRefreshingErrors();

        return throwError( () => error );
      } )
    );
  }

  private addTokenHeader( request: HttpRequest<Object>, token: string | null ) {
    return request.clone( {
      setHeaders: {
        Authorization: 'Bearer ' + token
      }
    } );
  }

  private isEmbeddedRoute(): boolean {
    return this.embeddedContextService.isEmbeddedRoute( this.router.url );
  }

  private getToken(): string | null {
    return this.isEmbeddedRoute() ? this.embeddedTokenService.getToken() : this.tokenService.getToken();
  }

  private getRefreshToken(): string | null {
    return this.isEmbeddedRoute() ? this.embeddedTokenService.getRefreshToken() : this.tokenService.getRefreshToken();
  }

  private saveToken( token: string ): void {
    if ( this.isEmbeddedRoute() ) {
      this.embeddedTokenService.saveToken( token );
      return;
    }

    this.tokenService.saveToken( token );
  }

  private saveRefreshToken( refreshToken: string ): void {
    if ( this.isEmbeddedRoute() ) {
      this.embeddedTokenService.saveRefreshToken( refreshToken );
      return;
    }

    this.tokenService.saveRefreshToken( refreshToken );
  }

  private refreshToken( tokens: Tokens ): Observable<Tokens> {
    return this.isEmbeddedRoute() ? this.embeddedAuthService.refreshToken( tokens ) : this.authService.refreshToken( tokens );
  }

  private handleRefreshFailure(): void {
    if ( this.isEmbeddedRoute() ) {
      this.embeddedTokenService.clearTokens();
      this.embeddedContextService.setSessionExpired();
      return;
    }

    this.authService.logout();
  }
}
